// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- Actions & Hooks
import { useJobsData } from "./hooks/useJobsData";
import { useRecursiveTimeout } from "../LicensingJobs/hooks/useRecursiveTimeout";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
// --- External UI Libraries
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
import { Col, Row } from "antd";
// --- Components
import { PaginationBar } from "../../components/PaginationBar";
import ToastNotificationProvider from "../../components/ToastNotifications";
import { JobsGrid } from "./components/grid";
import { JobsSearchForm } from "./components/search-form";
import TwoRowsLayout from "./two-row";
// --- Utilities
import { getItemRange } from "../../utils/pagination";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import { exportToCsv } from "../../utils/exportToCsv";
import { mapJobTypeToLabel } from "tap/utils/jobType.js";
// --- Config
import { JOBS_EXPORT_FIELDS, JOBS_EXPORT_RANGE } from "./config/export";
// --- Styles
import "./styles.scss";

// Auto-refresh interval in milliseconds (15 seconds)
const AUTO_REFRESH_INTERVAL = 15000;

/**
 * Jobs Page
 *
 * Features:
 * - Displays a paginated list of jobs
 * - Provides search and export functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 *
 * @returns {JSX.Element}
 */
export const JobsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchJobs, updateJobs, itemsPerPage, tapJobRange, filterForm } =
    useJobsData();
  const {
    totalPages,
    currentPage,
    totalItems,
    pageSizeOptions,
    isJobViewModalOpen,
    isTasksModalOpen,
    jobTypeOptions,
  } = useSelector((state) => state.tapJobs);

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.tapJobs,
    itemsPerPage,
    rangeKey: "tapJobRange",
    onSync: updateJobs,
  });

  const handleChange = async (pageNumber) => {
    if (pageNumber === currentPage) return;

    const nextPageRange = getItemRange(pageNumber, itemsPerPage);
    const data = await fetchJobs(nextPageRange);

    if (data) {
      updateJobs({
        jobs: data?.content,
        totalPages: data?.totalPages,
        currentPage: data?.pageable?.pageNumber,
        tapJobRange: nextPageRange,
        totalItems: data?.totalElements,
      });
    }
  };

  const handlePageSizeChange = (newSize) => {
    const currentParams = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...currentParams,
      pageSize: newSize,
    });
  };

  const handleRefresh = () => {
    updateJobs({ refreshJobs: true });
  };

  // Auto-refresh is paused while job view or tasks modals are open
  useRecursiveTimeout(
    handleRefresh,
    AUTO_REFRESH_INTERVAL,
    !isJobViewModalOpen && !isTasksModalOpen,
  );

  const handleExport = async () => {
    const data = await fetchJobs(JOBS_EXPORT_RANGE, filterForm);

    if (data) {
      const mappedContent = mapJobTypeToLabel(data?.content, jobTypeOptions);
      const filteredContent = mappedContent?.map((item) =>
        JOBS_EXPORT_FIELDS.reduce((obj, key) => {
          obj[key] = item[key];
          return obj;
        }, {}),
      );
      exportToCsv(filteredContent, "jobs");
    }
  };

  return (
    <>
      <Row className="jobs-page-header">
        <Col>
          <Typography variant="T50" style={{ marginLeft: 20 }}>
            TAP Jobs
          </Typography>
        </Col>
        <Col className="jobs-page-header-actions">
          <Button
            iconType="fa-solid fa-arrow-down-to-line"
            labelText="Export"
            styleType="link"
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            Export
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Col>
      </Row>
      <TwoRowsLayout>
        <main data-position="top">
          <JobsSearchForm />
        </main>
        <main data-position="bottom">
          <JobsGrid />
        </main>
      </TwoRowsLayout>
      <PaginationBar
        totalPages={totalPages}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handleChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
      <ToastNotificationProvider />
    </>
  );
};
